// covered-by: frontends/carrie/src/services/__tests__/enterprises.test.ts
// covered-by: frontends/carrie/src/routes/settings/routes/__tests__/EnterpriseSettingsView.test.tsx
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { switchEnterprise } from "../../services/auth";
import {
  createEnterprise,
  deleteEnterprise,
  duplicateEnterpriseAndWait,
  getEnterprises,
  getIntegrationHealth,
  getIntegrationSettings,
  getIntegrationEventStatus,
  getRecentIntegrationTasks,
  getAsanaProjects,
  registerAsanaWebhook,
  removeAsanaWebhook,
  triggerIntegrationTest,
  updateEnterprise,
  updateEnterpriseSettings,
  updateIntegrationSettings,
  type UpdateEnterpriseInput,
  type DuplicateEnterpriseInput,
} from "../../services/enterprises";
import { useAppDispatch } from "../hooks";
import { login } from "../slices/auth";

export function useCreateEnterprise() {
  const mutation = useMutation({
    mutationKey: ["create-enterprise"],
    mutationFn: createEnterprise,
  });

  return mutation;
}

export function useDuplicateEnterprise() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["duplicate-enterprise"],
    mutationFn: ({
      sourceEnterpriseId,
      ...body
    }: DuplicateEnterpriseInput & { sourceEnterpriseId: string }) =>
      duplicateEnterpriseAndWait(sourceEnterpriseId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["enterprises"] });
    },
  });
}

export function useDeleteEnterprise() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["delete-enterprise"],
    mutationFn: (
      input: string | { enterpriseId: string; cascade?: boolean },
    ) => {
      if (typeof input === "string") {
        return deleteEnterprise(input);
      }
      return deleteEnterprise(input.enterpriseId, { cascade: input.cascade });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["enterprises"] });
    },
  });
}

export function useUpdateEnterprise() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["update-enterprise"],
    mutationFn: ({
      enterpriseId,
      ...updates
    }: { enterpriseId: string } & UpdateEnterpriseInput) =>
      updateEnterprise(enterpriseId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["enterprises"] });
    },
  });
}

/** @deprecated Prefer useUpdateEnterprise */
export function useUpdateEnterpriseIsDemo() {
  const mutation = useUpdateEnterprise();
  return {
    ...mutation,
    mutateAsync: ({
      enterpriseId,
      isDemo,
    }: {
      enterpriseId: string;
      isDemo: boolean;
    }) => mutation.mutateAsync({ enterpriseId, isDemo }),
  };
}

export function useEnterprises() {
  return useQuery({
    queryKey: ["enterprises"],
    queryFn: getEnterprises,
  });
}

export function useSwitchEnterprise() {
  const dispatch = useAppDispatch();
  return useMutation({
    mutationKey: ["switch-enterprise"],
    mutationFn: switchEnterprise,
    onSuccess: (data) => {
      dispatch(login({ token: data.token }));
    },
  });
}

export function useUpdateEnterpriseSettings() {
  const mutation = useMutation({
    mutationKey: ["update-enterprise-settings"],
    mutationFn: updateEnterpriseSettings,
  });

  return mutation;
}

export function useIntegrationSettings() {
  return useQuery({
    queryKey: ["integration-settings"],
    queryFn: getIntegrationSettings,
  });
}

export function useUpdateIntegrationSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["update-integration-settings"],
    mutationFn: updateIntegrationSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integration-settings"] });
      queryClient.invalidateQueries({ queryKey: ["integration-health"] });
    },
  });
}

// Shape of the /private/catalyst/enterprises/integrations/health response.
// Kept here (not in @mg/schemas) because the backend handler returns a
// loosely-typed object today and we don't want to gate the Vitalora
// integrations panel on a backend schema refactor. Synthetic fields are
// only present for the demo tenant.
export interface ConnectionHealth {
  connected: boolean;
  status?: string;
  accountId?: string;
  updatedAt?: string | null;
  invocationCount?: number;
  isDisabled?: boolean;
  webhookCount?: number;
  totalWebhooks?: number;
}

interface SyntheticIntegrationHealth {
  connected: true;
  status: "active" | "read-only";
  meta?: string;
}

export interface IntegrationHealthResponse {
  wrike: ConnectionHealth | null;
  asana:
    | (ConnectionHealth & { webhookCount?: number; totalWebhooks?: number })
    | null;
  email?: {
    enabled: boolean;
    postmarkConfigured: boolean;
    aiEmailAddress: string | null;
  };
  slack?: SyntheticIntegrationHealth;
  figma?: SyntheticIntegrationHealth;
  googleDocs?: SyntheticIntegrationHealth;
  sharepoint?: SyntheticIntegrationHealth;
}

export function useIntegrationHealth() {
  return useQuery<IntegrationHealthResponse>({
    queryKey: ["integration-health"],
    queryFn: getIntegrationHealth as () => Promise<IntegrationHealthResponse>,
    staleTime: 60_000,
    retry: 1,
  });
}

export function useTriggerIntegrationTest() {
  return useMutation({
    mutationKey: ["trigger-integration-test"],
    mutationFn: triggerIntegrationTest,
  });
}

export function useIntegrationEventStatus(
  compositeTaskId: string | null,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ["integration-event-status", compositeTaskId],
    queryFn: () => getIntegrationEventStatus(compositeTaskId!),
    enabled: !!compositeTaskId && options?.enabled !== false,
    refetchInterval: (query) => {
      const data = query.state.data as { state?: string } | undefined;
      if (
        data?.state === "completed" ||
        data?.state === "failed" ||
        data?.state === "not_found"
      ) {
        return false;
      }
      return 5000;
    },
  });
}

export function useRecentIntegrationTasks(platform: string) {
  return useQuery({
    queryKey: ["recent-integration-tasks", platform],
    queryFn: () => getRecentIntegrationTasks(platform),
    staleTime: 30_000,
  });
}

export function useAsanaProjects(enabled = false) {
  return useQuery({
    queryKey: ["asana-projects"],
    queryFn: getAsanaProjects,
    enabled,
    staleTime: 60_000,
  });
}

export function useRegisterAsanaWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["register-asana-webhook"],
    mutationFn: registerAsanaWebhook,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integration-settings"] });
      queryClient.invalidateQueries({ queryKey: ["integration-health"] });
    },
  });
}

export function useRemoveAsanaWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["remove-asana-webhook"],
    mutationFn: removeAsanaWebhook,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integration-settings"] });
      queryClient.invalidateQueries({ queryKey: ["integration-health"] });
    },
  });
}
